import { inject, Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { ConfigurationTheme } from '../../../config/resources/configuration.resource';
import { UserPreferencesResource } from '../../../user/entities/user-preferences/user-preferences-resource';
import { UserPreferencesService } from '../../../user/services/user-preferences/user-preferences.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { ConfigService } from '../../../config/services/config.service';
import { LoggingService } from '../../../logging/logging.service';

type HSL = { h: number; s: number; l: number };

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private readonly configService = inject(ConfigService);
    private readonly userPreferencesService = inject(UserPreferencesService);
    private readonly log = inject(LoggingService).getLogger(this.constructor.name);

    constructor(private readonly storage: Storage) {
        this.storage.create().then((s) => {
            s.get('theme').then((cssText) => {
                this.setGlobalCSS(cssText);
            });
        });
        toObservable(this.userPreferencesService.userPreferences).subscribe((prefs) => {
            this.setBackgroundColor(prefs);
            this.setTextSize(prefs?.frontendSettings?.fontSize ?? 14);
        });
        toObservable(this.configService.config).subscribe((config) => {
            this.setColorFromEnvironment(config?.configuration?.theme);
        });
    }

    /**
     * This method is needed to initialize the service in app component.
     * Otherwise it loads lazily and doesn't initialize on app start.
     */
    init(): void {
        this.log.debug('Initialized theme service');
    }

    private setColorFromEnvironment(colorConfig: ConfigurationTheme): void {
        this.setColor(colorConfig, 'primary', '#0096C8');
        this.setColor(colorConfig, 'secondary', '#07357E');
    }

    async setTextSize(value: number = 14): Promise<void> {
        this.setGlobalCSS(this.CSSTextGenerator(value));
        await this.storage.set('theme', this.CSSTextGenerator(value)); // <--- SAVE THEME HERE
        // await this.storage.set('text-size', value); // <--- SAVE TEXT SIZE HERE
    }

    HexToHSL(hex: string): HSL {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) {
            throw new Error('Could not parse Hex Color');
        }

        const rHex = parseInt(result[1], 16);
        const gHex = parseInt(result[2], 16);
        const bHex = parseInt(result[3], 16);

        const r = rHex / 255;
        const g = gHex / 255;
        const b = bHex / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);

        let h = (max + min) / 2;
        let s = h;
        let l = h;

        if (max === min) {
            // Achromatic
            return { h: 0, s: 0, l };
        }

        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;

        s = s * 100;
        s = Math.round(s);
        l = l * 100;
        l = Math.round(l);
        h = Math.round(360 * h);

        return { h, s, l };
    }

    setBackgroundColor(userPreferences: UserPreferencesResource) {
        /* Set transparent background when no preferences (unauthenticated user) or MINI mode */
        if (!userPreferences || userPreferences.currentMode === 'MINI') {
            document.body.style.setProperty(`--main-content-background`, 'transparent');
        } else {
            document.body.style.setProperty(`--main-content-background`, '#ffffff');
        }
    }

    private setColor(colorConfig: ConfigurationTheme, colorName: 'primary' | 'secondary', defaultValue: string) {
        if (colorConfig?.color[colorName] && colorConfig.color[colorName] !== '') {
            this.setColorTemplate(colorName, colorConfig.color[colorName]);
            this.setColorInverseTemplate(colorName, colorConfig.color[colorName]);
        } else {
            this.setColorTemplate(colorName, defaultValue);
            this.setColorInverseTemplate(colorName + 'Contrast', '#ffffff');
        }
    }

    private setColorTemplate(type: string, color: string): void {
        if (!color) return;
        const shadeRatio = 0.2;
        const tintRatio = 0.1;
        document.body.style.setProperty(`--ion-color-${type}`, color);
        document.body.style.setProperty(`--ion-color-${type}-rgb`, this.hexToRgb(color));
        document.body.style.setProperty(`--ion-color-${type}-contrast`, '#ffffff');
        document.body.style.setProperty(`--ion-color-${type}-contrast-rgb`, this.hexToRgb('#ffffff'));
        document.body.style.setProperty(
            `--ion-color-${type}-shade`,
            this.HSLToHex(this.darken(this.HexToHSL(color), shadeRatio)),
        );
        document.body.style.setProperty(
            `--ion-color-${type}-tint`,
            this.HSLToHex(this.darken(this.HexToHSL(color), tintRatio)),
        );
    }

    private setColorInverseTemplate(type: string, color: string): void {
        if (!color) return;
        const shadeRatio = 0.2;
        const tintRatio = 0.1;
        document.body.style.setProperty(`--ion-color-${type + '-inverse'}`, '#ffffff');
        document.body.style.setProperty(`--ion-color-${type + '-inverse'}-rgb`, this.hexToRgb('#ffffff'));
        document.body.style.setProperty(`--ion-color-${type + '-inverse'}-contrast`, color);
        document.body.style.setProperty(`--ion-color-${type + '-inverse'}-contrast-rgb`, this.hexToRgb(color));
        document.body.style.setProperty(
            `--ion-color-${type + '-inverse'}-shade`,
            this.HSLToHex(this.darken(this.HexToHSL(color), shadeRatio)),
        );
        document.body.style.setProperty(
            `--ion-color-${type + '-inverse'}-tint`,
            this.HSLToHex(this.darken(this.HexToHSL(color), tintRatio)),
        );
    }

    private hexToRgb(hex: string): string {
        const result = /^#?([a-fd]{2})([a-fd]{2})([a-fd]{2})$/i.exec(hex);
        if (result) {
            const r = parseInt(result[1], 16);
            const g = parseInt(result[2], 16);
            const b = parseInt(result[3], 16);
            return `${r},${g},${b}`;
        }
        return null;
    }

    private CSSTextGenerator(size: number = 14): string {
        return `
    --curafida-text-size: ${size}px;
    --ion-font-size: ${size}px;
    `;
    }

    private HSLToHex(hsl: HSL): string {
        const { h, s, l } = hsl;

        const hDecimal = l / 100;
        const a = (s * Math.min(hDecimal, 1 - hDecimal)) / 100;
        const f = (n: number) => {
            const k = (n + h / 30) % 12;
            const color = hDecimal - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);

            // Convert to Hex and prefix with "0" if required
            return Math.round(255 * color)
                .toString(16)
                .padStart(2, '0');
        };
        return `#${f(0)}${f(8)}${f(4)}`;
    }

    private darken(hsl: HSL, ratio: number) {
        hsl.l -= hsl.l * ratio;
        return hsl;
    }

    private setGlobalCSS(css: string) {
        document.documentElement.style.cssText = css;
    }
}
